import { NavigationExtras } from '@angular/router';
import { compact, isFunction, omitBy } from 'lodash';
import { APPOINTMENTS_VIEW_ROUTE } from '../appointments/appointments.data';
import {
  BILLING_MANAGE_RENEW_DISABLE_ROUTE,
  BILLING_MANAGE_RENEW_ROUTE,
  BILLING_PAYMENT_ROUTE,
} from '../billing-data/billing-data.data';
import { CLIENTS_JOBS_ROUTE } from '../client-data/client-data.data';
import {
  DOCUMENTS_NEW_ROUTE,
  DOCUMENTS_PREVIEW_ROUTE,
  DOCUMENTS_SEND_ROUTE,
  MANUAL_PAYMENT_ACTION,
} from '../document-data/document-data.data';
import { INVOICES_UNMATCHED_PAYMENTS_ROUTE } from '../invoices/invoices.data';
import {
  APPOINTMENTS_MASTER_ROUTE,
  BILLING_MASTER_ROUTE,
  CLIENTS_MASTER_ROUTE,
  CREDIT_MEMOS_MASTER_ROUTE,
  ESTIMATES_MASTER_ROUTE,
  EXPENSES_MASTER_ROUTE,
  HELP_MASTER_ROUTE,
  HOME_MASTER_ROUTE,
  INVOICES_MASTER_ROUTE,
  ITEMS_MASTER_ROUTE,
  PAYWALL_MASTER_ROUTE,
  PURCHASE_ORDERS_MASTER_ROUTE,
  REPORTS_MASTER_ROUTE,
  SETTINGS_MASTER_ROUTE,
  TRACKED_TIMES_MASTER_ROUTE,
} from '../main/main.data';
import { REPORTS_TYPE_ROUTE } from '../report-data/report-data.data';
import {
  SETTINGS_CLIENT_COMMUNICATION_ROUTE,
  SETTINGS_CLIENT_PAYMENT_OPTIONS_ROUTE,
  SETTINGS_COMPANY_PROFILE_ROUTE,
  SETTINGS_COMPANY_REVIEWS_ROUTE,
  SETTINGS_INVOICE_DESIGN_ROUTE,
  SETTINGS_XERO_ROUTE,
} from '../settings/settings.data';
import { IRouter } from '../shared/interfaces/router.interface';
import { TRACKED_TIMES_VIEW_ROUTE } from '../tracked-times/tracked-times.constant';
import { IDeepLinkProperties } from './deep-link-properties.interface';
import { ScreenNamesEnum } from './screen-names.enum';

export const SCREEN_NAMES_TO_ROUTES: {
  [screenName: string]: (deepLinkProperties: IDeepLinkProperties) => IRouter;
} = {
  [ScreenNamesEnum.home]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [HOME_MASTER_ROUTE],
    };
  },
  [ScreenNamesEnum.invoice_list]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    // example -> i2g://navigate/invoice_list?filter=overdue&sort_by=due_date&sort_order=desc
    const paramsToRoute = {
      unpaid: 'unpaid',
      overdue: 'unpaid',
    };

    const route: string[] = [INVOICES_MASTER_ROUTE];
    const tab: string =
      paramsToRoute[
        deepLinkProperties.params['tab'] || deepLinkProperties.params['filter']
      ];
    if (tab) {
      route.push(tab);
    }
    const { sort_by, sort_order } = deepLinkProperties.params;

    return {
      route,
      navigationExtras: {
        queryParams: {
          sort_by,
          sort_order,
        },
      },
    };
  },
  [ScreenNamesEnum.invoice_create]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [INVOICES_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
      identifier: 'invoice',
    };
  },
  [ScreenNamesEnum.invoice_send]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        INVOICES_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        DOCUMENTS_SEND_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.invoice_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        INVOICES_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        DOCUMENTS_PREVIEW_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.invoice_record_transaction]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        INVOICES_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        DOCUMENTS_PREVIEW_ROUTE,
      ],
      navigationExtras: {
        queryParams: {
          action: MANUAL_PAYMENT_ACTION,
        },
      },
    };
  },
  [ScreenNamesEnum.unmatched_payments]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [INVOICES_MASTER_ROUTE, INVOICES_UNMATCHED_PAYMENTS_ROUTE],
    };
  },
  [ScreenNamesEnum.estimate_list]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [ESTIMATES_MASTER_ROUTE],
    };
  },
  [ScreenNamesEnum.estimate_create]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [ESTIMATES_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
    };
  },
  [ScreenNamesEnum.estimate_send]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        ESTIMATES_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        DOCUMENTS_SEND_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.estimate_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        ESTIMATES_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        DOCUMENTS_PREVIEW_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.credit_memo_list]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [CREDIT_MEMOS_MASTER_ROUTE],
    };
  },
  [ScreenNamesEnum.credit_memo_create]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [CREDIT_MEMOS_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
    };
  },
  [ScreenNamesEnum.credit_memo_send]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        CREDIT_MEMOS_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        DOCUMENTS_SEND_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.credit_memo_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        CREDIT_MEMOS_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        DOCUMENTS_PREVIEW_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.purchase_order_list]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [PURCHASE_ORDERS_MASTER_ROUTE],
    };
  },
  [ScreenNamesEnum.purchase_order_create]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [PURCHASE_ORDERS_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
    };
  },
  [ScreenNamesEnum.purchase_order_send]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        PURCHASE_ORDERS_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        DOCUMENTS_SEND_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.purchase_order_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        PURCHASE_ORDERS_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        DOCUMENTS_PREVIEW_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.client_list]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [CLIENTS_MASTER_ROUTE],
      identifier: 'client',
    };
  },
  [ScreenNamesEnum.client_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    const { id, filter } = deepLinkProperties.params;
    const navigationExtras: NavigationExtras = {};

    if (id && filter) {
      navigationExtras['queryParams'] = {
        filter,
      };
    }

    return {
      route: [CLIENTS_MASTER_ROUTE, id],
      identifier: 'client',
      navigationExtras,
    };
  },
  [ScreenNamesEnum.item_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [ITEMS_MASTER_ROUTE, deepLinkProperties.params['id']],
    };
  },
  [ScreenNamesEnum.expense_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [EXPENSES_MASTER_ROUTE, deepLinkProperties.params['id']],
    };
  },
  [ScreenNamesEnum.reports]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    const route: string[] = [
      REPORTS_MASTER_ROUTE,
      REPORTS_TYPE_ROUTE,
      deepLinkProperties.params['type'],
    ];
    const params = omitBy(
      deepLinkProperties.params,
      (value, key) => key === 'type' || isFunction(value)
    );

    return {
      route: route,
      navigationExtras: {
        queryParams: params,
      },
      identifier: 'report_list',
    };
  },
  [ScreenNamesEnum.settings]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    let route: string[] = [SETTINGS_MASTER_ROUTE];
    const param = deepLinkProperties.params['c'];

    if (param === 'company_info') {
      route.push(SETTINGS_COMPANY_PROFILE_ROUTE);
    } else if (param === 'template_editor') {
      route.push(SETTINGS_INVOICE_DESIGN_ROUTE);
    } else if (
      ['payment_options', 'card_payments', 'paypal_ec', 'ach_debit'].includes(
        param
      )
    ) {
      route.push(SETTINGS_CLIENT_PAYMENT_OPTIONS_ROUTE);
    } else if (['client_communication', 'payment_reminders'].includes(param)) {
      route.push(SETTINGS_CLIENT_COMMUNICATION_ROUTE);
    } else if (param === 'xero') {
      route.push(SETTINGS_XERO_ROUTE);
    } else if (param === 'company_reviews') {
      route.push(
        ...[SETTINGS_COMPANY_PROFILE_ROUTE, SETTINGS_COMPANY_REVIEWS_ROUTE]
      );
    } else if (param === 'cancel_subscription') {
      // open billing app with cancel subscription page
      return {
        route: [
          PAYWALL_MASTER_ROUTE,
          BILLING_MANAGE_RENEW_ROUTE,
          BILLING_MANAGE_RENEW_DISABLE_ROUTE,
        ],
      };
    } else if (param === 'unit_ach_bank_transfers') {
      route = null;
    }
    return { route };
  },
  [ScreenNamesEnum.paywall]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    if (deepLinkProperties.params.u) {
      return {
        route: [PAYWALL_MASTER_ROUTE],
        navigationExtras: {
          queryParams: deepLinkProperties.params,
        },
      };
    }

    return {
      route: [BILLING_MASTER_ROUTE],
    };
  },
  [ScreenNamesEnum.help]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [HELP_MASTER_ROUTE],
    };
  },
  [ScreenNamesEnum.purchase]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: compact([
        BILLING_MASTER_ROUTE,
        BILLING_PAYMENT_ROUTE,
        deepLinkProperties.params['plan_id'],
      ]),
    };
  },
  [ScreenNamesEnum.appointment_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        APPOINTMENTS_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        APPOINTMENTS_VIEW_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.appointment_list]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [APPOINTMENTS_MASTER_ROUTE],
    };
  },
  [ScreenNamesEnum.tracked_time_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    return {
      route: [
        TRACKED_TIMES_MASTER_ROUTE,
        deepLinkProperties.params['id'],
        TRACKED_TIMES_VIEW_ROUTE,
      ],
    };
  },
  [ScreenNamesEnum.tracked_time_list]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    const route: string[] = [TRACKED_TIMES_MASTER_ROUTE];
    const tab: string = deepLinkProperties.params['tab'];

    if (tab) {
      route.push(tab);
    }

    return { route };
  },
  [ScreenNamesEnum.update_tax_year]: (): IRouter => {
    return {
      route: [`${SETTINGS_MASTER_ROUTE}/${SETTINGS_COMPANY_PROFILE_ROUTE}`],
      navigationExtras: {
        queryParams: {
          action: 'update_tax_year',
        },
      },
    };
  },
  [ScreenNamesEnum.job_view]: (
    deepLinkProperties: IDeepLinkProperties
  ): IRouter => {
    const { id, client_id } = deepLinkProperties.params;
    return {
      route: [CLIENTS_MASTER_ROUTE, client_id, CLIENTS_JOBS_ROUTE, id],
    };
  },
  [ScreenNamesEnum.paypal_onboarding]: (): IRouter => {
    return {
      route: [SETTINGS_MASTER_ROUTE, SETTINGS_CLIENT_PAYMENT_OPTIONS_ROUTE],
    };
  },
};
